import React, { useCallback, useMemo } from 'react'
import { Input, FormGroup, Label } from 'reactstrap'

import ErrorBoundary from 'components/ErrorBoundary'
import Select from 'components/Select'
import ssapi from 'lib/api/secondstepApi'
import withDataV4 from 'lib/withDataV4'
import { NetSuiteCustomer } from 'types'

const NOT_LISTED = 'My school is not listed'
const SelectWithData = withDataV4(Select)

interface SchoolLookUpLayoutProps {
  city: string
  country: string
  disabled?: boolean
  hasRegionData: boolean
  netsuiteCustomerId: number | null
  organizationName: string
  region: string
  onFieldChange: (field: string, value: string) => void
  onLoadStateData: (data: string[]) => string[]
  onSchoolNameChange: (value: string) => void
  onSchoolSelect: (netSuiteCustomer: NetSuiteCustomer) => void
  onSelectCitiesEndpoint: () => Promise<{ status: number; data: string[] }>
  onSelectSchoolsEndpoint: () => Promise<{
    status: number
    data: NetSuiteCustomer[]
  }>
  onLoadSchoolsData: (data: NetSuiteCustomer[]) => NetSuiteCustomer[]
  getNetSuiteCustomerObject: (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => NetSuiteCustomer
  getNetsuiteCustomerId: () => string | number | null
}

const SchoolLookUpLayout: React.FC<SchoolLookUpLayoutProps> = ({
  city,
  country,
  disabled = false,
  hasRegionData,
  netsuiteCustomerId,
  organizationName,
  region,
  onFieldChange,
  onLoadStateData,
  onLoadSchoolsData,
  onSchoolNameChange,
  onSchoolSelect,
  onSelectCitiesEndpoint,
  onSelectSchoolsEndpoint,
  getNetSuiteCustomerObject,
  getNetsuiteCustomerId,
}) => {
  // Helper function to check if a field is available (not disabled)
  const isFieldAvailable = useCallback(
    (fieldName: string): boolean => {
      if (disabled) return false

      switch (fieldName) {
        case 'country':
          return true
        case 'region':
          return Boolean(country && hasRegionData)
        case 'city':
          return Boolean(country && (!hasRegionData || region))
        case 'netSuiteCustomerId':
          return Boolean(country && city)
        case 'organizationName':
          return Boolean(country && city)
        default:
          return false
      }
    },
    [disabled, country, region, city, hasRegionData],
  )

  // Derived functions from the availability state
  const isFieldDisabled = useCallback(
    (fieldName: string): boolean => !isFieldAvailable(fieldName),
    [isFieldAvailable],
  )
  const shouldShowRequired = useCallback(
    (fieldName: string): boolean => isFieldAvailable(fieldName),
    [isFieldAvailable],
  )

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const name = event.target.name
      const value = event.target.value
      onFieldChange(name, value)
    },
    [onFieldChange],
  )

  const handleSchoolNameChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onSchoolNameChange(event.target.value)
    },
    [onSchoolNameChange],
  )

  const handleSchoolSelect = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const netSuiteCustomer = getNetSuiteCustomerObject(event)
      onSchoolSelect(netSuiteCustomer)
    },
    [onSchoolSelect, getNetSuiteCustomerObject],
  )

  const handleLoadData = useCallback(
    (data: any[]) => {
      return onLoadStateData(data)
    },
    [onLoadStateData],
  )

  const renderSchoolOptions = useCallback((data: NetSuiteCustomer[]) => {
    const options: React.ReactElement[] = []

    // Add "My School is Not Listed" option first
    // Use string value '0' for consistency with HTML select elements
    options.push(
      <option key="0" value="0">
        {NOT_LISTED}
      </option>,
    )

    // Add all other school options (excluding the "not listed" option)
    // Handle both number and string types for id
    data.forEach(customer => {
      const id =
        typeof customer.id === 'number'
          ? customer.id
          : parseInt(String(customer.id), 10)
      if (id !== 0 && !isNaN(id)) {
        options.push(
          <option key={customer.id} value={String(customer.id)}>
            {customer.companyName}
          </option>,
        )
      }
    })

    return options
  }, [])

  // Memoize API methods to prevent unnecessary re-fetches
  const getCountriesApiMethod = useMemo(() => () => ssapi.getCountries(), [])

  const getRegionsApiMethod = useMemo(
    () => () =>
      country
        ? ssapi.getRegions(country)
        : Promise.resolve({ status: 200, data: [] }),
    [country],
  )

  return (
    <ErrorBoundary>
      <SelectWithData
        apiMethod={getCountriesApiMethod}
        disabled={disabled}
        enableShowRequired={shouldShowRequired('country')}
        label="Country *"
        name="country"
        onChange={handleChange}
        value={country}
      />

      <SelectWithData
        apiMethod={getRegionsApiMethod}
        disabled={isFieldDisabled('region')}
        enableShowRequired={shouldShowRequired('region')}
        label="State/Province/Territory *"
        name="region"
        onChange={handleChange}
        onLoadData={handleLoadData}
        value={region}
        dependencies={[country]}
      />

      <SelectWithData
        apiMethod={onSelectCitiesEndpoint}
        disabled={isFieldDisabled('city')}
        enableShowRequired={shouldShowRequired('city')}
        label="City *"
        name="city"
        onChange={handleChange}
        value={city}
        dependencies={[country, region]}
      />

      <SelectWithData
        apiMethod={onSelectSchoolsEndpoint}
        disabled={isFieldDisabled('netSuiteCustomerId')}
        enableShowRequired={shouldShowRequired('netSuiteCustomerId')}
        label="Assigned Site *"
        name="netSuiteCustomerId"
        onChange={handleSchoolSelect}
        onLoadData={onLoadSchoolsData}
        renderOptions={renderSchoolOptions}
        value={getNetsuiteCustomerId()}
        dependencies={[city, country, region]}
      />

      {netsuiteCustomerId === 0 && (
        <FormGroup>
          <Label for="organizationName">Organization * </Label>
          <Input
            disabled={isFieldDisabled('organizationName')}
            id="organizationName"
            name="organizationName"
            onChange={handleSchoolNameChange}
            value={organizationName}
          />
        </FormGroup>
      )}
    </ErrorBoundary>
  )
}

export default SchoolLookUpLayout
